import { z } from '@customerio/hydra-validation';
import type { IntegrationSetting } from '@/services/db/integration/entity';
import { isDataOutSettingRequired } from '@/services/db/data-out-integration/utils';

/**
 * The connector's fields are generated from its catalog settings schema, so only the *validation*
 * varies per connector — the object's shape is fixed. Keeping the shape in one schema and deriving
 * the form's value type from it means the two can't drift.
 */
const dataOutFormShape = z.object({
  settings: z.record(z.string(), z.union([z.string(), z.boolean()])),
  events: z.array(z.string()),
  enabled: z.boolean(),
  with_content: z.boolean(),
});

export type DataOutFormValues = z.infer<typeof dataOutFormShape>;
export type DataOutSettingValue = DataOutFormValues['settings'][string];

function isBlank(value: DataOutSettingValue | undefined): boolean {
  return typeof value !== 'boolean' && (value ?? '').trim().length === 0;
}

/**
 * A setting is required when its catalog `default` is `null`. Two carve-outs match the backend:
 *
 * - **Booleans are never required.** A checkbox always has a value, and the backend's own
 *   required-check only bites on missing keys.
 * - **A secret on an already-connected connector is optional.** The API never returns secret
 *   values, so the field renders empty against a stored value; requiring it would force the user to
 *   retype a secret to change anything else. A save that omits the key keeps the stored value.
 */
export function buildDataOutFormSchema(settings: readonly IntegrationSetting[], isConnected: boolean) {
  const required = settings.filter(
    (setting) => isDataOutSettingRequired(setting) && setting.type !== 'boolean' && !(isConnected && setting.secret),
  );

  return dataOutFormShape.superRefine((values, ctx) => {
    for (const setting of required) {
      if (isBlank(values.settings[setting.key])) {
        ctx.addIssue({
          code: 'custom',
          path: ['settings', setting.key],
          message: `${setting.label} can't be blank`,
        });
      }
    }
  });
}

/**
 * The settings map a save should actually send.
 *
 * The API never returns a stored secret, so its field renders blank against a real stored value.
 * Sending that blank back would be a destructive write — PUT merges settings over the stored ones,
 * so an empty string clears the credential and breaks the connector. Dropping the key instead is
 * what leaves the stored value alone, and it is the contract {@link buildDataOutFormSchema}'s
 * optional-secret carve-out already assumes.
 *
 * Only applies once connected: on first connect there is nothing stored to preserve, and the
 * backend fills its own defaults for any key the create omits.
 */
export function settingsForSave(
  settings: readonly IntegrationSetting[],
  values: Record<string, DataOutSettingValue>,
  isConnected: boolean,
): Record<string, DataOutSettingValue> {
  if (!isConnected) {
    return values;
  }

  const untouchedSecrets = new Set(
    settings.filter((setting) => setting.secret && isBlank(values[setting.key])).map((setting) => setting.key),
  );

  if (untouchedSecrets.size === 0) {
    return values;
  }

  return Object.fromEntries(Object.entries(values).filter(([key]) => !untouchedSecrets.has(key)));
}

/**
 * Whether "Test connection" can run. The backend needs every secret present to authenticate, and it
 * never sends stored secrets back, so a connected connector still requires them to be re-entered
 * before a test — even though a plain save does not.
 */
export function hasAllSecrets(
  settings: readonly IntegrationSetting[],
  values: Record<string, DataOutSettingValue>,
): boolean {
  return settings.every((setting) => !setting.secret || !isBlank(values[setting.key]));
}
